import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import type { ImageUploadItem, ImageUploadQueueOptions } from "./types";

type Listener = () => void;

const DEFAULT_CONCURRENCY = 6;

/**
 * 관찰자 콜백(계측·로깅)을 업로드 성패로부터 격리한다.
 *
 * 이 콜백들은 업로드 경로의 try/catch 안에서 호출되므로, 던지면 이미 성공한 업로드가
 * 실패로 뒤집히거나 실패 처리 도중 예외가 새어 나가 큐가 멈춘다.
 * 업로드 결과는 관찰자의 동작과 무관해야 하므로 여기서 삼키고 Sentry 로만 보고한다.
 */
function notifyObserver(label: string, notify: () => void): void {
  try {
    notify();
  } catch (error) {
    Sentry.captureException(error instanceof Error ? error : new Error(`ImageUploadQueue ${label} listener failed`), {
      extra: { listener: label },
    });
  }
}

/**
 * `accept` 패턴 하나와 파일을 대조한다. HTML `accept` 속성과 같은 표기를 지원한다.
 *
 * - `image/*` — MIME 접두사
 * - `image/heic` — MIME 완전 일치
 * - `.heic` — 확장자. iOS 에서 고른 파일은 `File.type` 이 빈 문자열로 오는 경우가 있어,
 *   MIME 만으로 판정하면 그런 파일이 큐에 들어오지도 못한 채 조용히 사라진다.
 */
export function matchesAcceptPattern(file: File, pattern: string): boolean {
  if (pattern.startsWith(".")) return file.name.toLowerCase().endsWith(pattern.toLowerCase());
  if (pattern.endsWith("/*")) return file.type.startsWith(pattern.slice(0, -1));
  return file.type === pattern;
}

/** `accept` 목록 중 하나라도 맞으면 true. 목록이 비어 있으면 제한 없음으로 본다. */
export function isAcceptedFile(file: File, accept: string[] | undefined): boolean {
  if (!accept?.length) return true;
  return accept.some((pattern) => matchesAcceptPattern(file, pattern));
}

/**
 * 프레임워크 비의존 이미지 업로드 큐.
 *
 * - `useSyncExternalStore` 호환 `subscribe` / `getSnapshot` 제공
 * - 모듈 레벨이나 Context 로 인스턴스를 유지하면
 *   컴포넌트 언마운트에도 업로드가 계속 진행됨
 * - `concurrency` 옵션으로 동시 업로드 수 제한 (기본 6)
 */
export class ImageUploadQueue {
  private _items: ImageUploadItem[] = [];
  private _listeners = new Set<Listener>();
  private _xhrs = new Map<string, XMLHttpRequest>();
  private _options: ImageUploadQueueOptions;
  private _seqCounter = 0;

  /** 현재 실행 중인 _uploadItem 개수 */
  private _activeCount = 0;
  /** 동시성 제한으로 대기 중인 아이템 */
  private _pendingQueue: ImageUploadItem[] = [];

  constructor(options: ImageUploadQueueOptions) {
    this._options = options;
  }

  /* ------------------------------------------------------------------ */
  /*  useSyncExternalStore 호환 인터페이스                                  */
  /* ------------------------------------------------------------------ */

  subscribe = (listener: Listener): (() => void) => {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  };

  getSnapshot = (): ImageUploadItem[] => {
    return this._items;
  };

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  updateOptions(options: Partial<ImageUploadQueueOptions>): void {
    this._options = { ...this._options, ...options };
  }

  addFiles = (files: FileList | File[]): void => {
    const fileArray = Array.from(files);
    const { maxFiles, maxSizeBytes, accept } = this._options;

    const available = maxFiles ? Math.max(0, maxFiles - this._items.length) : fileArray.length;
    const sliced = fileArray.slice(0, available);

    const newItems: ImageUploadItem[] = [];

    for (const file of sliced) {
      if (!isAcceptedFile(file, accept)) {
        continue;
      }

      if (maxSizeBytes && file.size > maxSizeBytes) {
        continue;
      }

      newItems.push({
        id: uuidv4(),
        seq: this._seqCounter++,
        file,
        previewUrl: URL.createObjectURL(file),
        status: "pending",
        progress: 0,
      });
    }

    if (newItems.length === 0) return;

    this._items = [...this._items, ...newItems];
    this._emit();

    this._pendingQueue.push(...newItems);
    this._drain();
  };

  removeItem = (id: string, options?: { revokePreviewUrl?: boolean }): void => {
    const item = this._items.find((i) => i.id === id);
    if (!item) return;
    const revokePreviewUrl = options?.revokePreviewUrl ?? true;

    // 아직 시작 전(대기 중)이면 큐에서만 제거 — _activeCount 변화 없음
    this._pendingQueue = this._pendingQueue.filter((i) => i.id !== id);

    // 업로드 중이면 XHR 취소 — _uploadItem의 finally가 _activeCount 감소 및 drain 담당
    this._xhrs.get(id)?.abort();
    this._xhrs.delete(id);

    if (revokePreviewUrl) URL.revokeObjectURL(item.previewUrl);
    this._items = this._items.filter((i) => i.id !== id);
    this._emit();

    // 진행 중(uploading)이거나 대기 중(pending)인 아이템을 제거한 경우에만 완료 여부를 체크.
    // 이미 종료된(success/error) 아이템 제거 시 _checkAllComplete를 호출하면
    // onSuccess / onAllComplete가 의도치 않게 재발화된다.
    if (item.status === "uploading" || item.status === "pending") {
      this._checkAllComplete();
    }
  };

  retryItem = (id: string): void => {
    const item = this._items.find((i) => i.id === id);
    if (!item || item.status !== "error") return;

    this._updateItem(id, { status: "pending", progress: 0, error: undefined });
    this._pendingQueue.push({ ...item, status: "pending", progress: 0, error: undefined });
    this._drain();
  };

  clear = (): void => {
    this._pendingQueue = [];

    for (const xhr of this._xhrs.values()) xhr.abort();
    this._xhrs.clear();

    for (const item of this._items) URL.revokeObjectURL(item.previewUrl);

    this._items = [];
    // 진행 중이던 _uploadItem의 finally가 나중에 실행되더라도 음수가 되지 않도록 즉시 리셋
    this._activeCount = 0;
    this._emit();
  };

  destroy = (): void => {
    this._listeners.clear();
    this.clear();
  };

  /* ------------------------------------------------------------------ */
  /*  Private                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * 동시성 한도 내에서 대기 중인 아이템을 순서대로 꺼내 업로드 시작.
   */
  private _drain(): void {
    const concurrency = this._options.concurrency ?? DEFAULT_CONCURRENCY;
    while (this._activeCount < concurrency && this._pendingQueue.length > 0) {
      this._activeCount++;
      this._uploadItem(this._pendingQueue.shift()!);
    }
  }

  private async _uploadItem(item: ImageUploadItem): Promise<void> {
    try {
      this._updateItem(item.id, { status: "uploading", progress: 0 });
      notifyObserver("onItemStart", () => this._options.onItemStart?.(item));

      const { uploadUrl, fileUrl } = await this._options.getPresignedUrl({ id: item.id, file: item.file });

      // presigned URL 발급 대기 중 removeItem이 호출됐을 수 있으므로 존재 여부 확인
      if (!this._items.some((i) => i.id === item.id)) return;

      await this._putFile(item.id, uploadUrl, item.file);

      this._updateItem(item.id, { status: "success", progress: 100, uploadedUrl: fileUrl });

      const completed = this._items.find((i) => i.id === item.id);
      if (completed) notifyObserver("onItemComplete", () => this._options.onItemComplete?.(completed));

      this._checkAllComplete();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;

      const message = error instanceof Error ? error.message : "Upload failed";
      this._updateItem(item.id, { status: "error", error: message });

      const failed = this._items.find((i) => i.id === item.id);
      if (failed) {
        notifyObserver("onError", () =>
          this._options.onError?.(error instanceof Error ? error : new Error(message), failed),
        );
      }

      this._checkAllComplete();
    } finally {
      // clear() 직후 abort가 늦게 감지되는 경우 음수 방지
      this._activeCount = Math.max(0, this._activeCount - 1);
      this._drain();
    }
  }

  /**
   * XMLHttpRequest 를 사용하여 presigned URL 에 PUT.
   * `fetch` 대비 모든 모바일 브라우저에서 upload progress 를 안정적으로 지원.
   */
  private _putFile(id: string, url: string, file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this._xhrs.set(id, xhr);

      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", file.type);

      let lastProgressEmit = Number.NEGATIVE_INFINITY;
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const now = performance.now();
        if (now - lastProgressEmit < 100) return;
        lastProgressEmit = now;
        this._updateItem(id, { progress: Math.round((e.loaded / e.total) * 100) });
      };

      xhr.onload = () => {
        this._xhrs.delete(id);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        this._xhrs.delete(id);
        reject(new Error("Network error"));
      };

      xhr.onabort = () => {
        this._xhrs.delete(id);
        reject(new DOMException("Upload aborted", "AbortError"));
      };

      xhr.send(file);
    });
  }

  private _updateItem(id: string, patch: Partial<Omit<ImageUploadItem, "id" | "file" | "previewUrl">>): void {
    const item = this._items.find((i) => i.id === id);
    if (!item) return;

    const hasChanged = Object.entries(patch).some(([key, value]) => item[key as keyof ImageUploadItem] !== value);
    if (!hasChanged) return;

    this._items = this._items.map((i) => (i.id === id ? { ...i, ...patch } : i));
    this._emit();
  }

  private _checkAllComplete(): void {
    if (this._items.length > 0 && this._items.every((i) => i.status === "success")) {
      this._options.onSuccess?.(this._items);
    }

    if (this._items.length > 0 && this._items.every((i) => i.status === "success" || i.status === "error")) {
      this._options.onAllComplete?.(this._items);
    }
  }

  private _emit(): void {
    for (const listener of this._listeners) listener();
  }
}

export function createImageUploadQueue(options: ImageUploadQueueOptions): ImageUploadQueue {
  return new ImageUploadQueue(options);
}
